import { BaseApiService } from './base';
import {
  Connection,
  CreateConnectionRequest,
  UpdateConnectionRequest,
  ConnectionFilters,
  ConnectionSortField,
  ConnectionTestResult,
  ConnectionStatus,
  ConnectionTestHistory,
  RateLimitStatus
} from '../../types/connection';
import { Task } from '../../types/task';
// THUB-280: validity check for a present-but-invalid provisioningStatus.
import { isProvisioningStatus } from '../../components/shared/StatusBadge';
import { PaginatedResponse, QueryParams } from '../../types/api';
import { snakeToCamel, camelToSnake } from '../../utils/caseTransform';
import {
  CascadeDeleteDependency,
  CascadeDeleteResult,
  DependencyError
} from './cascadeDelete';

// Re-export types for convenience
export type Dependency = CascadeDeleteDependency;
export type DeleteResult = CascadeDeleteResult;
export { DependencyError };

// T053: Helper to derive provisioning status from legacy status
function deriveProvisioningStatus(status: string): 'pending' | 'provisioning' | 'active' | 'failed' | 'deleting' {
  switch (status) {
    case 'active':
    case 'ready':
    case 'idle':
    case 'not_used':
    case 'issues':
      return 'active';
    case 'pending':
      return 'pending';
    case 'provisioning':
    case 'setting_up':
      return 'provisioning';
    case 'failed':
      return 'failed';
    case 'deleting':
      return 'deleting';
    // THUB-247: 'unknown' must NOT become 'pending'. It is the steady-state stored value for a
    // perfectly healthy connection -- azure_blob and api connections have no Transfer Family
    // connector to provision, so nothing ever advances the record past its initial value. Mapping
    // it to 'pending' made the NP Afficiency connection read "Pending" in the GUI while it was
    // actively delivering files to the partner.
    //
    // 'active' here means "provisioned", not "busy" -- which is why idle / not_used / issues
    // already map to it above. Liveness is a separate axis and is carried by `health`
    // (deriveHealthFromStatus keeps 'unknown' -> 'unknown', so we still do not claim it is
    // verified healthy).
    case 'unknown':
      return 'active';
    default:
      return 'pending';
  }
}

// T053: Helper to derive health from legacy status
function deriveHealthFromStatus(status: string): 'healthy' | 'warning' | 'error' | 'unknown' {
  switch (status) {
    case 'ready':
      return 'healthy';
    case 'idle':
    case 'not_used':
      return 'warning';
    case 'issues':
      return 'error';
    case 'setting_up':
    case 'unknown':
    default:
      return 'unknown';
  }
}

// Transform API response to camelCase Connection type using generic converter.
// Exported for direct unit testing (THUB-280) -- testing it through the service
// would require stubbing Amplify auth and config loading, which tests the
// transport rather than the mapping.
export function transformConnection(apiConnection: any): Connection {
  const conn = snakeToCamel<any>(apiConnection);
  const connectionType = conn.connectionType || 'outbound';
  const status = conn.status || 'unknown';

  // T053: Derive provisioning status and health from status if not explicitly provided.
  //
  // THUB-280: this is a VALIDITY check, not a truthiness check. The old `||` only
  // fell through when the field was absent, so a field that was PRESENT but held
  // an invalid value (the health values `unknown` / `not_used` that used to be
  // persisted into the provisioning field) bypassed the mapper entirely and
  // reached the badge as-is. `unknown` is truthy, so `||` cannot catch it.
  const provisioningStatus = isProvisioningStatus(conn.provisioningStatus)
    ? conn.provisioningStatus
    : deriveProvisioningStatus(conn.provisioningStatus || status);
  const health = conn.health || deriveHealthFromStatus(status);

  return {
    connectionId: conn.connectionId || '',
    name: conn.name || '',
    partnerId: conn.partnerId || '',
    partnerName: conn.partnerName || '',
    // FR-023: hostingType is the primary field
    hostingType: conn.hostingType || (connectionType === 'inbound' ? 'hosted' : 'remote'),
    transferMethod: conn.transferMethod || 'sftp',
    config: conn.config || {},
    status,
    provisioningStatus,
    health,
    lastConnectionTest: conn.lastConnectionTest,
    lastSuccessfulConnection: conn.lastSuccessfulConnection,
    connectionTestResults: conn.connectionTestResults
      ? transformTestResult(conn.connectionTestResults)
      : undefined,
    transferFamilyUserArn: conn.transferFamilyUserArn,
    transferFamilyConnectorArn: conn.transferFamilyConnectorArn,
    connectorEgressIps: conn.connectorEgressIps,
    taskCounts: conn.taskCounts || { total: 0, enabled: 0, disabled: 0 },
    // PGP Encryption (021-pgp-encryption)
    pgpPublicKeyConfigured: conn.pgpPublicKeyConfigured || false,
    pgpPrivateKeyConfigured: conn.pgpPrivateKeyConfigured || false,
    pgpKeyFingerprint: conn.pgpKeyFingerprint,
    pgpKeyExpiration: conn.pgpKeyExpiration,
    pgpKeyWarning: conn.pgpKeyWarning,
    createdAt: conn.createdAt || '',
    updatedAt: conn.updatedAt || '',
    createdBy: conn.createdBy || 'system',
    version: conn.version
  };
}

// Transform camelCase create request to snake_case for backend using generic converter
function transformCreateRequest(request: CreateConnectionRequest): any {
  const oauth2CustomParams = request.config?.oauth2CustomParams;
  const snake = camelToSnake<any>(request);
  // FR-023: Derive legacy connection_type for backward compatibility
  snake.connection_type = request.hostingType === 'hosted' ? 'inbound' : 'outbound';
  // Restore user-provided OAuth2 params (keys must not be case-converted)
  if (oauth2CustomParams !== undefined && snake.config) {
    snake.config.oauth2_custom_params = oauth2CustomParams;
  }
  return snake;
}

// Transform camelCase update request to snake_case for backend using generic converter
function transformUpdateRequest(request: UpdateConnectionRequest): any {
  const oauth2CustomParams = request.config?.oauth2CustomParams;
  const snake = camelToSnake<any>(request);
  // FR-023: Derive legacy connection_type for backward compatibility
  if (request.hostingType !== undefined) {
    snake.connection_type = request.hostingType === 'hosted' ? 'inbound' : 'outbound';
  }
  // Restore user-provided OAuth2 params (keys must not be case-converted)
  if (oauth2CustomParams !== undefined && snake.config) {
    snake.config.oauth2_custom_params = oauth2CustomParams;
  }
  return snake;
}

// Transform test result response to camelCase using generic converter
function transformTestResult(apiResponse: any): ConnectionTestResult {
  const raw = apiResponse.testResults || apiResponse.test_results || apiResponse;
  const r = snakeToCamel<any>(raw);
  return {
    success: r.success ?? false,
    responseTime: r.responseTime,
    error: r.error,
    testedAt: r.testedAt || '',
    errorCode: r.errorCode,
    errorStage: r.errorStage,
    stages: r.stages ? {
      hostReachable: r.stages.hostReachable,
      authentication: r.stages.authentication,
      directoryListing: r.stages.directoryListing,
    } : undefined,
  };
}

class ConnectionsApiService extends BaseApiService {
  // Connection CRUD operations
  async getConnections(filters?: ConnectionFilters, sortBy?: ConnectionSortField, sortOrder?: 'asc' | 'desc'): Promise<Connection[]> {
    const params: QueryParams = {};
    
    if (filters) {
      params.filters = [];
      
      if (filters.status.length > 0) {
        params.filters.push({
          field: 'status',
          operator: 'in',
          value: filters.status
        });
      }
      
      if (filters.hostingType.length > 0) {
        params.filters.push({
          field: 'hostingType',
          operator: 'in',
          value: filters.hostingType
        });
      }
      
      if (filters.transferMethod.length > 0) {
        params.filters.push({
          field: 'transferMethod',
          operator: 'in',
          value: filters.transferMethod
        });
      }
      
      if (filters.partners.length > 0) {
        params.filters.push({
          field: 'partnerId',
          operator: 'in',
          value: filters.partners
        });
      }
    }
    
    if (sortBy && sortOrder) {
      params.sort = [{
        field: sortBy,
        order: sortOrder
      }];
    }

    const response = await this.get('/connections', params);
    const data = await response.json(); return (data.connections || []).map(transformConnection);
  }

  async getConnectionsPaginated(
    page: number = 1,
    limit: number = 20,
    filters?: ConnectionFilters,
    sortBy?: ConnectionSortField,
    sortOrder?: 'asc' | 'desc'
  ): Promise<PaginatedResponse<Connection>> {
    const params: QueryParams = {
      pagination: { page, limit, sortBy, sortOrder }
    };
    
    if (filters) {
      params.filters = [];
      
      if (filters.status.length > 0) {
        params.filters.push({
          field: 'status',
          operator: 'in',
          value: filters.status
        });
      }
      
      if (filters.hostingType.length > 0) {
        params.filters.push({
          field: 'hostingType',
          operator: 'in',
          value: filters.hostingType
        });
      }
      
      if (filters.transferMethod.length > 0) {
        params.filters.push({
          field: 'transferMethod',
          operator: 'in',
          value: filters.transferMethod
        });
      }
      
      if (filters.partners.length > 0) {
        params.filters.push({
          field: 'partnerId',
          operator: 'in',
          value: filters.partners
        });
      }
    }

    return this.getPaginated<PaginatedResponse<Connection>>('/connections', params);
  }

  async getConnection(id: string): Promise<Connection> {
    const response = await this.get(`/connections/${id}`);
    const data = await response.json();
    return transformConnection(data.connection || data);
  }

  async createConnection(connectionData: CreateConnectionRequest): Promise<Connection> {
    const requestData = transformCreateRequest(connectionData);
    const response = await this.post('/connections', requestData);
    // Response may contain 'connection' key or be the connection directly
    const data = await response.json(); const connectionResponse = data.connection || data;
    return transformConnection(connectionResponse);
  }

  async updateConnection(id: string, connectionData: UpdateConnectionRequest): Promise<Connection> {
    const requestData = transformUpdateRequest(connectionData);
    const response = await this.put(`/connections/${id}`, requestData);
    // Response may contain 'connection' key or be the connection directly
    const data = await response.json(); const connectionResponse = data.connection || data;
    return transformConnection(connectionResponse);
  }

  /**
   * Deletes a connection. If cascade is false and the connection has dependencies,
   * throws a DependencyError with the list of dependencies.
   * @param id - The connection ID
   * @param options - Delete options
   * @param options.cascade - If true, deletes all dependent tasks before deleting the connection
   * @returns Delete result with optional cascade deletion summary
   * @throws DependencyError if connection has dependencies and cascade is false
   */
  async deleteConnection(id: string, options?: { cascade?: boolean; version?: number }): Promise<DeleteResult> {
    const params = new URLSearchParams();
    if (options?.cascade) params.set('cascade', 'true');
    if (options?.version !== undefined) params.set('version', String(options.version));
    const qs = params.toString();
    const url = `/connections/${id}${qs ? `?${qs}` : ''}`;

    try {
      const response = await this.delete(url);
      const data = await response.json();
      return {
        success: true,
        message: data.message || 'Connection deleted successfully',
        cascadeDeleted: data.cascadeDeleted
      };
    } catch (err: any) {
      // Check for dependency errors in the ApiError details (HTTP 409).
      // THUB-167 follow-up: the backend returns error='CANNOT_DELETE_WITH_CHILDREN'
      // with a `children` count map (not a `dependencies` array). Previously this
      // path only matched 'HAS_DEPENDENCIES' and read `details.dependencies`, so the
      // 409 fell through to a generic inline error and the CascadeDeleteDialog never
      // opened — making GUI cascade-delete of a connection-with-tasks impossible.
      // Mirror the working partners.ts logic: accept both codes and synthesize the
      // dependency list from the children counts when the backend only sends counts.
      if (err.statusCode === 409 || err.code === 'CONFLICT') {
        const details = err.details || {};
        if (details.error === 'CANNOT_DELETE_WITH_CHILDREN' || details.error === 'HAS_DEPENDENCIES') {
          const dependencies: CascadeDeleteDependency[] = details.dependencies || [];
          const children = details.children || {};
          const taskCount = children.tasks || 0;
          const totalCount = details.dependencyCount || taskCount;

          // If backend only provided counts (not individual items), create placeholders
          // so the cascade dialog still renders the affected-resource list.
          if (dependencies.length === 0 && totalCount > 0) {
            for (let i = 0; i < taskCount; i++) {
              dependencies.push({ type: 'task', id: `task-${i + 1}`, name: `Task ${i + 1}` });
            }
          }

          throw new DependencyError(
            details.message || 'Connection has dependent resources that must be deleted first.',
            dependencies,
            totalCount,
            details.hint || 'Use cascade=true to delete all dependent resources.'
          );
        }
      }
      // Re-throw other errors
      throw err;
    }
  }

  // Connection testing and health monitoring
  async testConnection(id: string): Promise<ConnectionTestResult> {
    const response = await this.post(`/connections/${id}/test`);
    const data = await response.json();
    return transformTestResult(data);
  }

  // 018-thub-5: Rotate SSH key for remote connection
  // THUB-113: Accept optional keyType to select algorithm
  async rotateKey(id: string, keyType?: 'rsa' | 'ecdsa'): Promise<{
    connectionId: string;
    generatedPublicKey: string;
    keyGenerationMethod: string;
    rotatedAt: string;
    message: string;
  }> {
    const body = keyType ? { key_type: keyType } : undefined;
    const response = await this.post(`/connections/${id}/rotate-key`, body);
    return response.json();
  }

  async getConnectionStatus(id: string): Promise<ConnectionStatus> {
    const response = await this.get(`/connections/${id}/status`);
    const data = await response.json(); return data.status;
  }

  async refreshConnectionStatus(id: string): Promise<ConnectionStatus> {
    const response = await this.post(`/connections/${id}/refresh-status`);
    const data = await response.json(); return data.status;
  }

  // Connection tasks
  async getConnectionTasks(id: string): Promise<Task[]> {
    const response = await this.get(`/connections/${id}/tasks`);
    // Transform snake_case API response to camelCase Task objects
    const data = await response.json(); 
    return (data.tasks || []).map((raw: any) => {
      const task = snakeToCamel<any>(raw);
      return {
        taskId: task.taskId || '',
        name: task.name || '',
        partnerId: task.partnerId || '',
        partnerName: task.partnerName || '',
        connectionId: task.connectionId || '',
        connectionName: task.connectionName || '',
        transferDirection: task.transferDirection ||
          (task.transferType === 'receive' || task.transferType === 'pickup' ? 'inbound' : 'outbound'),
        status: task.status || 'disabled',
        configuration: task.config || task.configuration || {},
        schedule: task.schedule || undefined,
        lastExecution: task.lastExecution || undefined,
        metrics: {
          totalExecutions: task.runCount || 0,
          successfulExecutions: task.successCount || 0,
          failedExecutions: task.failureCount || 0,
          averageExecutionTime: task.avgExecutionTime || 0,
          totalFilesProcessed: task.totalFilesProcessed || 0,
          totalBytesTransferred: task.totalBytesTransferred || 0,
          lastSuccessfulExecution: task.lastSuccessAt || undefined,
          lastFailedExecution: task.lastFailureAt || undefined
        },
        eventBridgeRuleArn: task.eventBridgeRuleArn || undefined,
        createdAt: task.createdAt || '',
        updatedAt: task.updatedAt || '',
        createdBy: task.createdBy || 'system'
      };
    });
  }

  // Connection analytics
  async getConnectionAnalytics(id: string): Promise<{
    totalTasks: number;
    enabledTasks: number;
    disabledTasks: number;
    successRate: number;
    averageResponseTime: number;
    uptime: number;
    lastTestResults: ConnectionTestResult[];
  }> {
    const response = await this.get(`/connections/${id}/analytics`);
    return response.json();
  }

  // Get connections by partner
  async getConnectionsByPartner(partnerId: string): Promise<Connection[]> {
    const params: QueryParams = {
      filters: [{
        field: 'partnerId',
        operator: 'eq',
        value: partnerId
      }]
    };

    const response = await this.get('/connections', params);
    const data = await response.json();
    const connections = (data.connections || []).map(transformConnection);
    // Client-side filter as safety net in case backend doesn't apply filter
    return connections.filter((c: Connection) => c.partnerId === partnerId);
  }

  // Connection validation
  async validateConnectionConfiguration(
    connectionData: CreateConnectionRequest | UpdateConnectionRequest
  ): Promise<{ valid: boolean; errors: string[] }> {
    const response = await this.post(
      '/connections/validate-configuration',
      connectionData
    );
    return response.json();
  }

  async checkConnectionNameAvailability(name: string, partnerId: string, excludeId?: string): Promise<{ available: boolean }> {
    const params: QueryParams = {
      filters: [
        { field: 'name', operator: 'eq', value: name },
        { field: 'partnerId', operator: 'eq', value: partnerId }
      ]
    };
    
    if (excludeId) {
      params.filters!.push({ field: 'excludeId', operator: 'eq', value: excludeId });
    }

    const response = await this.get('/connections/check-name', params);
    return response.json();
  }

  // Connection statistics
  async getConnectionStatistics(): Promise<{
    totalConnections: number;
    activeConnections: number;
    inactiveConnections: number;
    connectionsByType: Record<string, number>;
    connectionsByMethod: Record<string, number>;
  }> {
    const response = await this.get('/connections/statistics');
    return response.json();
  }

  /**
   * Retrieves the connection test history for a specific connection.
   * Returns up to 100 test records sorted by date (newest first).
   * Test history is retained for 30 days via DynamoDB TTL.
   * @param id - The connection ID
   * @returns Array of test history records with results and metadata
   */
  async getConnectionTestHistory(id: string): Promise<ConnectionTestHistory[]> {
    const response = await this.get(`/connections/${id}/test-history`);
    const data = await response.json();
    return (data.testHistory || []).map((raw: any) => {
      const item = snakeToCamel<any>(raw);
      return {
        testId: item.testId,
        connectionId: item.connectionId,
        partnerId: item.partnerId,
        testType: item.testType || 'manual',
        testResults: {
          success: item.testResults?.success ?? false,
          responseTime: item.testResults?.responseTime,
          error: item.testResults?.error,
          testedAt: item.testResults?.testedAt ?? item.createdAt,
          errorCode: item.testResults?.errorCode,
          errorStage: item.testResults?.errorStage,
          stages: item.testResults?.stages
        },
        triggeredBy: item.triggeredBy || 'user',
        userId: item.userId,
        createdAt: item.createdAt
      };
    });
  }

  /**
   * Tests a connection configuration without saving it (for create/edit forms).
   * Validates the configuration and optionally performs a connectivity test
   * if an existing connector/user ARN is provided (edit mode).
   * @param params - Configuration to test
   * @param params.connectionType - 'inbound' or 'outbound'
   * @param params.transferMethod - 'sftp', 'ftp', 's3', or 'api'
   * @param params.config - Connection configuration (host, port, credentials, etc.)
   * @param params.connectorArn - For edit mode: existing Transfer Family connector ARN
   * @param params.userArn - For edit mode: existing Transfer Family user ARN
   * @returns Validation result with optional test results
   */
  async testConnectionConfig(params: {
    connectionType: string;
    transferMethod: string;
    config: any;
    connectorArn?: string;
    userArn?: string;
  }): Promise<{
    isValid: boolean;
    validationErrors: string[];
    testResults?: any;
    testPerformed: boolean;
  }> {
    const oauth2CustomParams = params.config?.oauth2CustomParams;
    const snakeConfig = camelToSnake<any>(params.config);
    if (oauth2CustomParams !== undefined) {
      snakeConfig.oauth2_custom_params = oauth2CustomParams;
    }
    const response = await this.post('/connections/test-config', {
      connectionType: params.connectionType,
      transferMethod: params.transferMethod,
      config: snakeConfig,
      connectorArn: params.connectorArn,
      userArn: params.userArn
    });
    return response.json();
  }

  // 021-pgp-encryption: Upload PGP keys to a connection
  async uploadPgpKeys(id: string, pgpData: {
    pgpPublicKey?: string;
    pgpPrivateKey?: string;
    pgpPrivateKeyPassphrase?: string;
  }): Promise<Connection> {
    const requestData = camelToSnake<any>(pgpData);
    const response = await this.put(`/connections/${id}`, requestData);
    const data = await response.json();
    const connectionResponse = data.connection || data;
    return transformConnection(connectionResponse);
  }

  /**
   * Gets the rate limit status for a connection's test functionality.
   * Calculates status from connection data without making additional API calls.
   * Rate limits include:
   * - 30-second cooldown between tests on the same connection
   * - Concurrent test prevention (only one test at a time per connection)
   * @param id - The connection ID
   * @returns Rate limit status including whether testing is allowed
   */
  async getRateLimitStatus(id: string): Promise<RateLimitStatus> {
    const connection = await this.getConnection(id);
    const now = Date.now() / 1000;

    // Calculate rate limit status from connection data
    let testInProgress = connection.testInProgress || false;
    let rateLimited = false;
    let retryAfter: number | undefined;

    // Check if lock has expired
    if (testInProgress && connection.testInProgressTtl) {
      if (now >= connection.testInProgressTtl) {
        testInProgress = false;
      }
    }

    // Check rate limit cooldown (30 seconds)
    if (connection.lastTestRateLimitAt && !testInProgress) {
      const lastTestTime = new Date(connection.lastTestRateLimitAt).getTime() / 1000;
      const elapsed = now - lastTestTime;
      const cooldown = 30; // 30 seconds

      if (elapsed < cooldown) {
        rateLimited = true;
        retryAfter = Math.ceil(cooldown - elapsed);
      }
    }

    return {
      testInProgress,
      rateLimited,
      retryAfter
    };
  }
}

// Export singleton instance
export const connectionsApi = new ConnectionsApiService();